import { useEffect, useRef, useMemo } from 'react';
import { useForm }           from 'react-hook-form';
import { buildDynamicResolver } from '../validator';
import { useRuleEngine }        from './useRuleEngine';

export function useFieldEngine(fieldConfigs, initialValues = {}, contextValues = {}) {
  const defaultValues = useMemo(() => buildDefaultValues(fieldConfigs, initialValues), [fieldConfigs, initialValues])

  const methods = useForm({
    defaultValues,
    values: defaultValues,
    resetOptions: {
      keepDefaultValues: true,
      keepDirty: true,
      keepTouched: true,
      keepErrors: false
    },
    resolver: buildDynamicResolver(fieldConfigs, contextValues),
    shouldUnregister: true,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const derivedState = useRuleEngine(methods.control, fieldConfigs, contextValues);

  const prevForcedValues  = useRef({});
  const prevDisabledState = useRef({});

  useEffect(() => {
    if (!derivedState) return;

    Object.entries(derivedState).forEach(([fieldKey, fieldState]) => {
      const forced = fieldState.forcedValue;
      if (forced !== undefined && forced !== prevForcedValues.current[fieldKey]) {
        methods.setValue(fieldKey, forced, { shouldValidate: false, shouldDirty: true });
        prevForcedValues.current[fieldKey] = forced;
      }

      const wasDisabled = prevDisabledState.current[fieldKey];
      const isNowDisabled = fieldState.disabled;
      if (wasDisabled === false && isNowDisabled === true) {
        const config = fieldConfigs.find(c => c.fieldKey === fieldKey);
        const resetVal = config?.defaultValue ?? '';
        methods.setValue(fieldKey, resetVal, { shouldValidate: false, shouldDirty: false });
      }
      prevDisabledState.current[fieldKey] = isNowDisabled;
    });
  }, [derivedState, methods]);

  return { methods, derivedState };
}

function buildDefaultValues(fieldConfigs, initialValues) {
  if (!Array.isArray(fieldConfigs)) return {};
  const iv = initialValues || {}

  const defaults = {};
  fieldConfigs.forEach(config => {
    defaults[config.fieldKey] = resolveInitialValue(config, iv)
  });
  return defaults;
}

function resolveInitialValue(config, iv) {
  const {fieldKey, name, defaultValue, options} = config
  const candidates = [fieldKey]

  const stripped = strippedHashSuffix(fieldKey)
  if(stripped && stripped !== fieldKey) candidates.push(stripped)

  if(name && !candidates.includes(name)) candidates.push(name)

  for(const key of candidates) {
      const v = iv[key]
      if(v !== undefined && v !== null) return canonicalizeOptionValue(v, options)
  }
  return defaultValue ?? ''
}

// Edit-mode values come straight from the backend and may differ in case/whitespace
// from the option's canonical `value` (which is what onChange writes and what the
// rule engine compares against). Without this, a hydrated value can render as
// "selected" (SelectField matches loosely for display) while every rule/effect tied
// to it silently fails to fire until the user re-picks the option manually.
function canonicalizeOptionValue(value, options) {
  if (!Array.isArray(options) || options.length === 0) return value

  if (Array.isArray(value)) return value.map(v => canonicalizeOptionValue(v, options))

  const normalized = String(value ?? '').trim().toLowerCase()
  const match = options.find(o => String(o.value ?? '').trim().toLowerCase() === normalized)
  return match ? match.value : value
}


function strippedHashSuffix(fieldKey) {
  if(typeof fieldKey !== 'string') return fieldKey
  return fieldKey.replace(/_[a-f0-9]{6,}$/i, '')
}